/**
 * =============================================================================
 * SCRIPT-NAME:   AdsKeywordManager.js
 * PROJEKT:       Freiseinapp - Google Ads Strategie-Backbone
 * BESCHREIBUNG:  Zentrale UI zur Keyword-Steuerung. 
 * Unterstützt Batch-Aktivierung mit UTM-Label-Generierung (ddmm--hhmm).
 * Synchronisiert mit der Datenbank-Struktur: kw, kw_clean, qf, imp, klicks, conv, kosten.
 *
 * VERSION:       v1.14 (FULL INTEGRITY & BATCH-SYNC)
 * DATUM:         30.01.2026
 * AUTHOR:        KI-Core (Synchronisiert mit User-Vorgabe)
 * =============================================================================
 */

import React, { useState, useEffect } from 'react';
import BackButton from "./BackButton";
import "./AdminDashboard.css";
import "./AdsKeywordManager.css";

export default function AdsKeywordManager() {
    // --- STATES ---
    const [kampagne, setKampagne] = useState('Verlustangst');
    const [keywords, setKeywords] = useState([]);
    const [status, setStatus] = useState({ type: '', msg: '' });
    const [loading, setLoading] = useState(false);
    
    // Batch-Steuerung (wie im Asset-Manager)
    const [globalDate, setGlobalDate] = useState(new Date().toISOString().split('T')[0]);
    const [globalTime, setGlobalTime] = useState("10:00");
    
    const token = localStorage.getItem("token");

    // Initiales Laden
    useEffect(() => {
        loadData();
    }, [kampagne]);

    /**
     * API-GET: Lädt den Bestand
     */
    const loadData = async () => {
        setLoading(true);
        setStatus({ type: 'loading', msg: 'Analysiere Datenbank-Bestand...' });
        try {
            const response = await fetch(`https://freiseinapp.de/api/ads_keywords_api.php?kampagne=${encodeURIComponent(kampagne)}`, {
                headers: { "Authorization": `Bearer ${token}` }
            });
            const result = await response.json();
            
            if (result.success && result.data) {
                setKeywords(result.data);
                setStatus({ type: 'success', msg: `${result.data.length} Keywords aktiv.` });
            } else {
                setKeywords([]);
                setStatus({ type: 'info', msg: 'Matrix bereit für neue Strategie.' });
            }
        } catch (e) {
            setStatus({ type: 'error', msg: 'Verbindung zur API fehlgeschlagen.' });
        }
        setLoading(false);
    };

    /**
     * API-POST: Synchronisiert die gesamte Matrix als Batch
     */
    const handleSave = async () => {
        if (keywords.length === 0) {
            setStatus({ type: 'error', msg: 'Keine Keywords zum Speichern vorhanden.' });
            return;
        }

        setLoading(true);
        setStatus({ type: 'loading', msg: 'Generiere UTM-Label & Aktiviere Batch...' });
        
        try {
            const response = await fetch('https://freiseinapp.de/api/ads_keywords_api.php', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    kampagne,
                    global_date: globalDate,
                    global_time: globalTime,
                    keywords: keywords.filter(k => k.kw && k.kw.trim() !== '') 
                })
            });
            const result = await response.json();
            
            if (result.success) {
                setStatus({ type: 'success', msg: `🚀 Batch erfolgreich aktiviert! UTM-Label: ${result.utm_label}` });
                loadData();
            } else {
                setStatus({ type: 'error', msg: 'Server-Fehler: ' + result.message });
            }
        } catch (e) {
            setStatus({ type: 'error', msg: 'Netzwerkfehler beim Speichern.' });
        }
        setLoading(false);
    };

    /**
     * Reinigung: [Keyword] -> keyword
     */
    const cleanKeywordLogic = (val) => {
        if (!val) return "";
        return val.toLowerCase().trim().replace(/[\[\]"]/g, '');
    };

    const addKeywordSlot = () => {
        setKeywords([...keywords, {
            kw: '', kw_clean: '', qf: 7, imp: 0, klicks: 0, conv: 0, kosten: 0
        }]);
    };

    const updateField = (idx, field, value) => {
        const newKws = [...keywords];
        if (field === 'kw') {
            newKws[idx].kw = value;
            newKws[idx].kw_clean = cleanKeywordLogic(value);
        } else {
            newKws[idx][field] = value;
        }
        setKeywords(newKws);
    };

    const calculateCTR = (klicks, imp) => {
        const i = parseInt(imp) || 0;
        if (i === 0) return "0.00";
        return (((parseInt(klicks) || 0) / i) * 100).toFixed(2);
    };

    return (
        <div className="analytics-page-wrapper">
            <div className="analytics-dashboard-container" style={{maxWidth: '1400px'}}>
                
                <header className="analytics-header">
                    <h2>🔑 Ads Keyword Manager</h2>
                </header>

                {/* BATCH HEADER (Analog zu Asset Manager) */}
                <div className="date-filter-section" style={{background: '#2a2a2a', color: '#eee', borderRadius: '8px', padding: '20px', display: 'flex', gap: '20px', alignItems: 'flex-end', border: '1px solid #444'}}>
                    <div style={{flex: 1}}>
                        <label style={{color: '#00d4ff', fontWeight: 'bold', fontSize: '0.8em'}}>Kampagne</label>
                        <select 
                            className="funnel-lp-select" 
                            value={kampagne} 
                            onChange={(e) => setKampagne(e.target.value)}
                            style={{background: '#333', color: '#fff', border: '1px solid #444'}}
                        >
                            <option value="Verlustangst">Verlustangst</option>
                            <option value="Eifersucht">Eifersucht</option>
                        </select>
                    </div>
                    <div style={{flex: 1}}>
                        <label style={{color: '#aaa', fontSize: '0.8em'}}>Aktivierungs-Datum (Freitag)</label>
                        <input 
                            type="date" 
                            className="date-input" 
                            value={globalDate} 
                            onChange={(e) => setGlobalDate(e.target.value)} 
                            style={{background: '#333', color: '#fff', border: '1px solid #444'}}
                        />
                    </div>
                    <div style={{flex: 1}}>
                        <label style={{color: '#aaa', fontSize: '0.8em'}}>Uhrzeit</label>
                        <input 
                            type="time" 
                            className="date-input" 
                            value={globalTime} 
                            onChange={(e) => setGlobalTime(e.target.value)} 
                            style={{background: '#333', color: '#fff', border: '1px solid #444'}}
                        />
                    </div>
                    <div style={{display: 'flex', gap: '10px'}}>
                        <button onClick={addKeywordSlot} className="analyze-button" style={{backgroundColor: '#4a5568'}}>
                            ➕ Neue Zeile
                        </button>
                        <button onClick={handleSave} className="analyze-button" disabled={loading} style={{background: '#00d4ff', color: '#000', fontWeight: 'bold'}}>
                            {loading ? "Warte..." : "🚀 Batch aktivieren"}
                        </button>
                    </div>
                </div>

                {status.msg && <div className={`message-bar ${status.type}`} style={{marginTop: '20px'}}>{status.msg}</div>}

                {/* GRID PFLEGE-BEREICH */}
                <div className="keyword-grid" style={{marginTop: '30px'}}>
                    {keywords.map((k, idx) => {
                        const ctr = calculateCTR(k.klicks, k.imp);
                        const isDead = (parseInt(k.imp) === 0 && k.kw !== "");

                        return (
                            <div key={idx} className={`keyword-card ${isDead ? 'is-dead' : ''}`}>
                                <div className="card-header">
                                    <div className="form-group">
                                        <label>QF</label>
                                        <select className="match-type-badge" value={k.qf} onChange={(e) => updateField(idx, 'qf', e.target.value)}>
                                            {[...Array(11).keys()].map(n => <option key={n} value={n}>{n}/10</option>)}
                                        </select>
                                    </div>
                                    <div className="ctr-display" style={{ color: parseFloat(ctr) > 3 ? '#28a745' : '#dc3545' }}>
                                        {ctr}% CTR
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Keyword (Ads)</label>
                                    <input 
                                        type="text" 
                                        className="keyword-input" 
                                        value={k.kw} 
                                        onChange={(e) => updateField(idx, 'kw', e.target.value)} 
                                        placeholder='[begriff] oder "phrase"' 
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Clean-Version</label>
                                    <input type="text" className="keyword-input kw-clean-display" value={k.kw_clean} readOnly tabIndex="-1" />
                                </div>

                                <div className="kpi-grid">
                                    <div className="form-group"><label>Imp</label><input type="number" className="kpi-input" value={k.imp} onChange={(e) => updateField(idx, 'imp', e.target.value)} /></div>
                                    <div className="form-group"><label>Klicks</label><input type="number" className="kpi-input" value={k.klicks} onChange={(e) => updateField(idx, 'klicks', e.target.value)} /></div>
                                    <div className="form-group"><label>Conv</label><input type="number" className="kpi-input" value={k.conv} onChange={(e) => updateField(idx, 'conv', e.target.value)} /></div>
                                    <div className="form-group"><label>Euro</label><input type="number" step="0.01" className="kpi-input" value={k.kosten} onChange={(e) => updateField(idx, 'kosten', e.target.value)} /></div>
                                </div>

                                {isDead && <div className="dead-warning">⚠️ NULL-TOLERANZ: Asset Tot!</div>}
                            </div>
                        );
                    })}
                </div>

                {/* PERFORMANCE SUMMARY */}
                <div className="lp-performance-section" style={{marginTop: '50px'}}>
                    <h3>📈 Strategie-Check: {kampagne}</h3>
                    <div className="table-wrapper">
                        <table className="analytics-table">
                            <thead>
                                <tr>
                                    <th>Keyword</th>
                                    <th>Impressionen</th>
                                    <th>Conversions</th>
                                    <th>CPA (€)</th>
                                    <th>Status / Aktion</th>
                                </tr>
                            </thead>
                            <tbody>
                                {keywords.filter(k => k.kw !== "").map((k, i) => {
                                    const cpa = k.conv > 0 ? (k.kosten / k.conv).toFixed(2) : "-";
                                    return (
                                        <tr key={i}>
                                            <td><strong>{k.kw_clean}</strong></td>
                                            <td>{k.imp}</td>
                                            <td>{k.conv}</td>
                                            <td>{cpa} €</td>
                                            <td>
                                                {parseInt(k.imp) === 0 ? <span className="drop-off-value">💀 Asset Kill</span> : 
                                                 k.conv === 0 && k.klicks > 15 ? <span style={{color:'#f39c12'}}>⚠️ Flaschenhals</span> : '✅ OK'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                <footer className="dashboard-footer" style={{marginTop: '50px'}}>
                    <BackButton to="/admin" />
                </footer>
            </div>
        </div>
    );
}